import { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { SubmitHandler } from 'react-hook-form'
import { Button, Spinner } from 'react-bootstrap'
import {
  EntityWithSearch,
  EntityWithSearchFields
} from '../../components/organisms/EntityFilterWithSearch'
import { EntityList } from '../../components/organisms/EntityList'
import { PaymentTableCard } from '../../components/organisms/PaymentsTableCard'
import { IGetPayments, PaginatedResponse } from '../../domain/usecases/Payments'
import { useDispatch } from 'react-redux'
import { Receivable } from '../../domain/models/ReceivableModel'
import { AppContext } from '../../app'
import {
  downloadRelatorioCompleto,
  checkReportStatus,
  fetchUserSpes,
  getUserEmailFromToken,
  ReportFilters,
  SpeOption
} from '../../services/reports'
import { ReportDownloadModal } from '../../components/molecules/ReportDownloadModal'
import {
  Pagination,
  PaginationInfo
} from '../../components/organisms/Pagination'

export type PaymentsProps = {
  remoteGetPayments: IGetPayments
}

export type Filters = {
  company_name?: string
  spe_name?: string
  project_name?: string
  start_date?: string
  end_date?: string
  status?: string[]
  page?: number
  limit?: number
}

const Payments = ({ remoteGetPayments }: PaymentsProps) => {
  const [payments, setPayments] = useState<Receivable[]>([])
  const [isDownloading, setIsDownloading] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [hasActiveJob, setHasActiveJob] = useState(false)
  const [reportSuccess, setReportSuccess] = useState(false)
  const [spes, setSpes] = useState<SpeOption[]>([])
  const [loadingSpes, setLoadingSpes] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const { user } = useContext(AppContext)
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  })
  const [filters, setFilters] = useState<Filters>({
    page: 1,
    limit: 10
  })

  const dispatch = useDispatch()

  useEffect(() => {
    const getPayments = async () => {
      try {
        dispatch({ type: 'SET_LOADING', loading: true })
        const response = await remoteGetPayments.getPayments(filters)
        console.log('payments response', response)

        // Handle both paginated and non-paginated responses for backwards compatibility
        if (
          response &&
          typeof response === 'object' &&
          'data' in response &&
          'pagination' in response
        ) {
          // New paginated response format
          setPayments(response.data || [])
          setPagination(response.pagination)
        } else if (Array.isArray(response)) {
          // Legacy array response format
          const paymentsArray = response as Receivable[]
          setPayments(paymentsArray)
          setPagination({
            page: 1,
            limit: paymentsArray.length,
            total: paymentsArray.length,
            totalPages: 1,
            hasNext: false,
            hasPrevious: false
          })
        } else {
          // Fallback for unexpected response
          console.error('Unexpected API response format:', response)
          setPayments([])
          setPagination({
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrevious: false
          })
        }
      } catch (error) {
        console.error('Error fetching payments:', error)
        setPayments([])
        setPagination({
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        })
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false })
      }
    }

    getPayments()
  }, [filters])

  const handleSendSubmitFilter: SubmitHandler<EntityWithSearchFields> = (
    values
  ) => {
    const { companyName, speName, projectName, endDate, startDate, status } =
      values

    setFilters({
      company_name: companyName,
      project_name: projectName,
      start_date:
        startDate && moment(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
      end_date: endDate && moment(endDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
      spe_name: speName,
      status,
      page: 1, // Reset to first page when applying filters
      limit: filters.limit
    })
  }

  // Verifica se o usuário tem permissão para exportar relatórios de recebíveis
  const hasExportPermission =
    user?.selectedContext?.permissions?.includes('receivables.export_report') ??
    false

  const handleOpenReportModal = async () => {
    setReportSuccess(false)
    setShowReportModal(true)

    if (user?.token) {
      // Get email from JWT token (works even for sessions before deploy)
      setUserEmail(getUserEmailFromToken(user.token))

      // Check active job and load SPEs in parallel
      setLoadingSpes(true)
      try {
        const [status, spesData] = await Promise.all([
          checkReportStatus(user.token, user.selectedContextEncoded),
          fetchUserSpes(user.token, user.selectedContextEncoded)
        ])
        setHasActiveJob(status.hasActiveJob)
        setSpes(spesData)
      } catch {
        setHasActiveJob(false)
      } finally {
        setLoadingSpes(false)
      }
    }
  }

  const handleDownloadReport = async (
    reportFilters: ReportFilters,
    emailOverride?: string
  ) => {
    if (!user?.token) {
      alert('Erro: Usuario nao autenticado')
      return
    }

    setIsDownloading(true)
    try {
      await downloadRelatorioCompleto(user.token, user.selectedContextEncoded, {
        dataInicio: reportFilters.dataInicio,
        dataFim: reportFilters.dataFim,
        sourceFilter: reportFilters.sourceFilter,
        speId: reportFilters.speId,
        forceUauRefresh: reportFilters.forceUauRefresh,
        emailOverride
      })
      setReportSuccess(true)
      setHasActiveJob(true)
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setHasActiveJob(true)
      } else {
        console.error('Erro ao solicitar relatorio:', error)
        alert('Erro ao solicitar relatorio. Tente novamente.')
      }
    } finally {
      setIsDownloading(false)
    }
  }

  // Só mostra o botão se o usuário tiver permissão
  const downloadButton = hasExportPermission ? (
    <Button
      variant="success"
      onClick={handleOpenReportModal}
      disabled={isDownloading}
      style={{ marginLeft: '10px' }}
    >
      {isDownloading ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            style={{ marginRight: '5px' }}
          />
          Baixando...
        </>
      ) : (
        <>
          <i className="feather icon-download" style={{ marginRight: '5px' }} />
          Baixar Relatorio Excel
        </>
      )}
    </Button>
  ) : null

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page
    }))
  }

  const handleLimitChange = (limit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit,
      page: 1 // Reset to first page when changing limit
    }))
  }

  return (
    <>
      <EntityList
        mainTitle="Recebíveis"
        title="Contratos de Compra e Venda"
        buttons={downloadButton}
        entityTable={
          <>
            <PaymentTableCard values={payments} />
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </>
        }
        filterWithSearch={
          <EntityWithSearch onSubmit={handleSendSubmitFilter} />
        }
        activeFilter={4}
      />
      {hasExportPermission && (
        <ReportDownloadModal
          show={showReportModal}
          onHide={() => setShowReportModal(false)}
          onDownload={handleDownloadReport}
          loading={isDownloading}
          hasActiveJob={hasActiveJob}
          success={reportSuccess}
          spes={spes}
          loadingSpes={loadingSpes}
          userEmail={userEmail}
        />
      )}
    </>
  )
}

export default Payments
